import { ArrowDown, ArrowUp, Plus, Trash2, X } from "lucide-react";
import { GetImageUrl } from "../helpers/GetImageUrl";
import { getVideoEmbedUrl } from "../helpers/videoEmbed";

const updateAt = (setSections, index, changes) =>
  setSections((prev) =>
    prev.map((section, sectionIndex) =>
      sectionIndex === index ? { ...section, ...changes } : section,
    ),
  );

const newCard = () => ({ key: "", value: "", title: "", description: "" });

export const createPageSection = (type = "content") => ({
  type,
  heading: "",
  subHeading: "",
  description: "",
  mediaType: "",
  image: { url: "", publicId: "", alt: "" },
  video: { url: "", publicId: "", title: "", description: "", thumbnail: "" },
  cards: [],
});

export default function AdminContentSectionEditor({ sections, setSections, inputClass }) {
  const moveSection = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= sections.length) return;

    setSections((prev) => {
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const chooseImage = (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const section = sections[index];
    if (section?.imagePreview?.startsWith?.("blob:")) URL.revokeObjectURL(section.imagePreview);

    updateAt(setSections, index, {
      imageFile: file,
      imagePreview: URL.createObjectURL(file),
      video: { ...(section.video || {}), url: "", publicId: "" },
      mediaType: "image",
    });
    event.target.value = "";
  };

  const setVideoUrl = (index, url) => {
    updateAt(setSections, index, {
      imageFile: null,
      imagePreview: "",
      image: { ...(sections[index].image || {}), url: "", publicId: "" },
      video: { ...(sections[index].video || {}), url, publicId: "" },
      mediaType: url ? "video" : "",
    });
  };

  const removeMedia = (index, type) => {
    const section = sections[index];
    if (section?.imagePreview?.startsWith?.("blob:")) URL.revokeObjectURL(section.imagePreview);

    updateAt(setSections, index, {
      imageFile: null,
      imagePreview: "",
      [type]: { ...(section[type] || {}), url: "", publicId: "" },
      mediaType: "",
    });
  };

  const updateCard = (sectionIndex, cardIndex, changes) =>
    setSections((prev) =>
      prev.map((section, index) =>
        index !== sectionIndex
          ? section
          : {
              ...section,
              cards: section.cards.map((card, index) =>
                index === cardIndex ? { ...card, ...changes } : card,
              ),
            },
      ),
    );

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">3. Dynamic Page Sections</p>
          <h4 className="mt-1 text-lg font-bold text-white">Build content and feature sections in visitor order</h4>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setSections((prev) => [...prev, createPageSection("content")])} className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white">+ Content Section</button>
          <button type="button" onClick={() => setSections((prev) => [...prev, createPageSection("features")])} className="rounded bg-cyan-700 px-3 py-2 text-sm font-semibold text-white">+ Feature Section</button>
        </div>
      </div>

      {sections.map((section, sectionIndex) => {
        const isFeature = section.type === "features";
        const mediaType = section.imageFile || section.image?.url ? "image" : section.video?.url ? "video" : "";
        const preview = mediaType === "image" ? section.imagePreview || GetImageUrl(section.image?.url) : section.video?.url;
        const embedUrl = mediaType === "video" ? getVideoEmbedUrl(preview) : "";
        const contentIndex = sections.slice(0, sectionIndex).filter((entry) => entry.type !== "features").length;

        return (
          <article key={section._id || sectionIndex} className="rounded-xl border border-white/10 bg-white/4 p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Section {sectionIndex + 1}: {isFeature ? "Feature Section" : "Content Section"}</p>
                {!isFeature && <p className="mt-1 text-sm font-semibold text-cyan-300">Frontend layout: {contentIndex % 2 === 0 ? "Text Left / Media Right" : "Media Left / Text Right"}</p>}
              </div>
              <div className="flex gap-2">
                <button type="button" disabled={!sectionIndex} onClick={() => moveSection(sectionIndex, -1)} className="rounded bg-white/10 p-2 disabled:opacity-30"><ArrowUp size={15} /></button>
                <button type="button" disabled={sectionIndex === sections.length - 1} onClick={() => moveSection(sectionIndex, 1)} className="rounded bg-white/10 p-2 disabled:opacity-30"><ArrowDown size={15} /></button>
                <button type="button" onClick={() => setSections((prev) => prev.filter((_, index) => index !== sectionIndex))} className="rounded bg-red-600 p-2"><Trash2 size={15} /></button>
              </div>
            </div>

            <select value={section.type} onChange={(event) => updateAt(setSections, sectionIndex, { type: event.target.value })} className={inputClass}>
              <option value="content">Content Section</option>
              <option value="features">Feature Section</option>
            </select>

            {!isFeature && (
              <div className="mt-4 rounded-lg border border-dashed border-white/20 p-4">
                <p className="mb-3 text-sm font-semibold text-white">Section Media</p>
                <div className="flex flex-wrap gap-2">
                  <label className="cursor-pointer rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white">
                    {mediaType === "image" ? "Change Image" : "+ Add Image"}
                    <input type="file" accept="image/*" className="hidden" onChange={(event) => chooseImage(event, sectionIndex)} />
                  </label>
                  {mediaType && <button type="button" onClick={() => removeMedia(sectionIndex, mediaType)} className="rounded bg-red-600 px-3 py-2 text-xs font-semibold">Remove {mediaType}</button>}
                </div>

                <input
                  value={section.video?.url || ""}
                  onChange={(event) => setVideoUrl(sectionIndex, event.target.value)}
                  placeholder="Video URL"
                  className={`${inputClass} mt-3`}
                />

                {preview && (
                  <div className="mt-4 max-w-md overflow-hidden rounded-lg bg-black">
                    {mediaType === "image" ? (
                      <img src={preview} alt={section.image?.alt || section.heading || "Section preview"} className="h-48 w-full object-cover" />
                    ) : embedUrl ? (
                      <iframe src={embedUrl} title={section.video?.title || "Video preview"} className="aspect-video w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
                    ) : (
                      <video src={preview} controls preload="metadata" className="max-h-64 w-full" />
                    )}
                  </div>
                )}

                {mediaType === "image" && <input value={section.image?.alt || ""} onChange={(event) => updateAt(setSections, sectionIndex, { image: { ...(section.image || {}), alt: event.target.value } })} placeholder="Image alt text" className={`${inputClass} mt-3`} />}
                {mediaType === "video" && (
                  <div className="mt-3 grid gap-2">
                    <input value={section.video?.title || ""} onChange={(event) => updateAt(setSections, sectionIndex, { video: { ...(section.video || {}), title: event.target.value } })} placeholder="Video title" className={inputClass} />
                    <textarea value={section.video?.description || ""} onChange={(event) => updateAt(setSections, sectionIndex, { video: { ...(section.video || {}), description: event.target.value } })} placeholder="Video description" rows={2} className={inputClass} />
                    <input value={section.video?.thumbnail || ""} onChange={(event) => updateAt(setSections, sectionIndex, { video: { ...(section.video || {}), thumbnail: event.target.value } })} placeholder="Thumbnail URL" className={inputClass} />
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 grid gap-3">
              <input value={section.heading || ""} onChange={(event) => updateAt(setSections, sectionIndex, { heading: event.target.value })} placeholder="Section heading" className={inputClass} />
              <textarea value={section.description || ""} onChange={(event) => updateAt(setSections, sectionIndex, { description: event.target.value })} placeholder="Section description" rows={3} className={inputClass} />
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">{isFeature ? "Features" : "Cards"} ({section.cards?.length || 0})</p>
                <button type="button" onClick={() => updateAt(setSections, sectionIndex, { cards: [...(section.cards || []), newCard()] })} className="rounded bg-white/10 px-3 py-2 text-xs font-semibold"><Plus size={14} className="mr-1 inline" /> Add {isFeature ? "Feature" : "Card"}</button>
              </div>
              {(section.cards || []).map((card, cardIndex) => (
                <div key={card._id || cardIndex} className="grid gap-2 rounded-lg bg-black/10 p-3">
                  <div className="flex justify-between">
                    <p className="text-xs font-bold uppercase text-slate-400">{isFeature ? "Feature" : "Card"} {cardIndex + 1}</p>
                    <button type="button" onClick={() => updateAt(setSections, sectionIndex, { cards: section.cards.filter((_, index) => index !== cardIndex) })} className="text-red-400"><X size={16} /></button>
                  </div>
                  <input value={card.title || card.key || ""} onChange={(event) => updateCard(sectionIndex, cardIndex, { title: event.target.value, key: event.target.value })} placeholder="Card title" className={inputClass} />
                  <textarea value={card.description || card.value || ""} onChange={(event) => updateCard(sectionIndex, cardIndex, { description: event.target.value, value: event.target.value })} placeholder="Card description" rows={2} className={inputClass} />
                </div>
              ))}
            </div>
          </article>
        );
      })}
    </section>
  );
}
